/**
 * Estado visual de câmera/microfone/áudio na chamada (tiles DOM + helpers).
 */

import { cameraOffPresenceCopy } from "./videoCallCopy";
import { floorPresenceCopy, voFloorFirstName } from "./voFloorLiving";
import { DEFAULT_CLOCK_TZ, voCommStatusMark } from "../VirtualOffice/voCommunicationStatus";
import { userIdFromAgentIdentity } from "./voOfficeLife";

const CAM_OFF_PATH =
  "M21 6.5l-4 4V7c0-.55-.45-1-1-1H9.82L21 17.18V6.5zM3.27 2 2 3.27 4.73 6H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.21 0 .39-.08.54-.18L19.73 21 21 19.73 3.27 2z";
const MIC_OFF_PATH =
  "M19 11h-1.7c0 .74-.16 1.43-.43 2.05l1.23 1.23c.56-.98.9-2.09.9-3.28zm-4.02.17c0-.06.02-.11.02-.17V5c0-1.66-1.34-3-3-3S9 3.34 9 5v.18l5.98 5.99zM4.27 3 3 4.27l6.01 6.01V11c0 1.66 1.33 3 2.99 3 .22 0 .44-.03.65-.08l1.66 1.66c-.71.33-1.5.52-2.31.52-2.76 0-5.3-2.1-5.3-5.1H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c.91-.13 1.77-.45 2.54-.9L19.73 21 21 19.73 4.27 3z";
const SPEAKER_OFF_PATH =
  "M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3 3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4 9.91 6.09 12 8.18V4z";

function mediaOffSvg(kind, px) {
  const size = Math.max(8, Number(px) || 11);
  const d = kind === "muted" ? MIC_OFF_PATH : kind === "speakerMuted" ? SPEAKER_OFF_PATH : CAM_OFF_PATH;
  return `<svg viewBox="0 0 24 24" width="${size}" height="${size}" aria-hidden="true"><path fill="currentColor" d="${d}"/></svg>`;
}

export const VO_MEDIA_FLAG_COPY = {
  muted: "Microfone desligado",
  camOff: "Câmera desligada",
  speakerMuted: "Áudio do escritório mutado",
};

/** Ícones discretos no assento: só o que a pessoa marcou. */
export function voFloorMediaFlags({ muted = false, camOff = false, speakerMuted = false } = {}) {
  const flags = [];
  if (muted) flags.push({ key: "muted", title: VO_MEDIA_FLAG_COPY.muted });
  if (camOff) flags.push({ key: "camOff", title: VO_MEDIA_FLAG_COPY.camOff });
  if (speakerMuted) flags.push({ key: "speakerMuted", title: VO_MEDIA_FLAG_COPY.speakerMuted });
  return flags;
}

/** Pende os chips na borda da foto para não cobrir o rosto quando o assento encolhe. */
export function voFloorMediaFlagsPlacement({ crowd = "roomy", compact = false, camOn = false } = {}) {
  const cam = compact ? 52 : voFloorSeatBoxSize({ camOn, crowd }).cam;
  const chipPx = cam <= 56 ? 12 : cam <= 72 ? 13 : cam <= 88 ? 14 : 16;
  return {
    cam,
    chipPx,
    iconPx: Math.max(8, chipPx - 3),
    hangPx: Math.round(chipPx * (cam <= 72 ? 0.72 : 0.55)),
  };
}

export function sanitizeAvatarUrl(url) {
  const s = String(url || "").trim();
  if (!s || s.length > 500) return "";
  if (/^https?:\/\//i.test(s)) return s;
  if (s.startsWith("//") && /^\/\/[A-Za-z0-9._-]/.test(s)) return `https:${s}`;
  if (s.startsWith("/") && !s.startsWith("//") && !s.startsWith("/\\")) return s;
  return "";
}

export function videoInitials(name) {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${(parts[0][0] || "").toUpperCase()}${(parts[parts.length - 1][0] || "").toUpperCase()}`;
}

export function mediaStateFromParticipant(participant, isScreenShareTrack) {
  let hasCameraTrack = false;
  let hasAudioTrack = false;
  let camOn = false;
  let micOn = false;
  const pubs = [];
  participant?.tracks?.forEach?.((pub) => pubs.push(pub));
  participant?.videoTracks?.forEach?.((pub) => {
    if (!pubs.includes(pub)) pubs.push(pub);
  });
  participant?.audioTracks?.forEach?.((pub) => {
    if (!pubs.includes(pub)) pubs.push(pub);
  });
  for (const pub of pubs) {
    const kind = pub?.kind || pub?.track?.kind;
    const track = pub?.track;
    if (kind === "video") {
      if (typeof isScreenShareTrack === "function" && isScreenShareTrack(track || pub)) continue;
      hasCameraTrack = true;
      if (pub.isSubscribed !== false && track && track.isEnabled !== false) camOn = true;
    }
    if (kind === "audio") {
      hasAudioTrack = true;
      if (pub.isSubscribed !== false && track && track.isEnabled !== false) micOn = true;
    }
  }
  return {
    hasCameraTrack,
    hasAudioTrack,
    camOff: !camOn,
    muted: !micOn,
  };
}

export function resolveTileMediaState(fromTracks, announced) {
  const camOff = fromTracks?.hasCameraTrack
    ? Boolean(fromTracks.camOff)
    : announced?.camOff != null
      ? Boolean(announced.camOff)
      : Boolean(fromTracks?.camOff ?? true);
  const muted = fromTracks?.hasAudioTrack
    ? Boolean(fromTracks.muted)
    : announced?.muted != null
      ? Boolean(announced.muted)
      : Boolean(fromTracks?.muted ?? true);
  const speakerMuted =
    announced?.speakerMuted != null
      ? Boolean(announced.speakerMuted)
      : Boolean(fromTracks?.speakerMuted);
  return { camOff, muted, speakerMuted };
}

function applyStyle(el, styles) {
  Object.assign(el.style, styles);
}

export const VO_FLOOR_AVATAR_PX = 96;
export const VO_FLOOR_LIVE_CAM_PX = 168;
export const VO_FLOOR_SEAT_WIDTH_PX = 188;
export const VO_FLOOR_SEAT_HEIGHT_PX = 228;
export const VO_FLOOR_LIVE_SEAT_WIDTH_PX = 220;
export const VO_FLOOR_LIVE_SEAT_HEIGHT_PX = 260;
export const VO_FLOOR_SEAT_WIDTH_XS = 96;
export const VO_FLOOR_SEAT_HEIGHT_XS = 128;
export const VO_FLOOR_LIVE_SEAT_WIDTH_XS = 112;
export const VO_FLOOR_LIVE_SEAT_HEIGHT_XS = 152;

/**
 * Densidade do tapete até o teto da Twilio (50). Pouca gente = assento grande;
 * 50 pessoas cabem em poucas fileiras sem estourar a cena.
 */
export const VO_FLOOR_CROWD_SIZES = {
  roomy: {
    max: 8,
    gap: { xs: 1, md: 2.5 },
    idle: { xs: [96, 128], sm: [150, 188], md: [188, 228], cam: 96 },
    live: { xs: [112, 152], sm: [196, 240], md: [220, 260], cam: 168 },
  },
  cozy: {
    max: 16,
    gap: { xs: 0.75, md: 1.5 },
    idle: { xs: [84, 112], sm: [124, 156], md: [148, 184], cam: 80 },
    live: { xs: [96, 128], sm: [148, 180], md: [172, 208], cam: 124 },
  },
  busy: {
    max: 28,
    gap: { xs: 0.5, md: 1 },
    idle: { xs: [72, 96], sm: [96, 124], md: [112, 140], cam: 64 },
    live: { xs: [80, 108], sm: [108, 136], md: [128, 156], cam: 88 },
  },
  packed: {
    max: 50,
    gap: { xs: 0.5, md: 0.75 },
    idle: { xs: [58, 80], sm: [72, 96], md: [80, 104], cam: 48 },
    live: { xs: [64, 88], sm: [80, 104], md: [92, 116], cam: 60 },
  },
};

export function voFloorCrowdTier(peopleCount) {
  const n = Math.max(1, Number(peopleCount) || 1);
  if (n <= VO_FLOOR_CROWD_SIZES.roomy.max) return "roomy";
  if (n <= VO_FLOOR_CROWD_SIZES.cozy.max) return "cozy";
  if (n <= VO_FLOOR_CROWD_SIZES.busy.max) return "busy";
  return "packed";
}

function voFloorCrowdSpec(crowd = "roomy") {
  return VO_FLOOR_CROWD_SIZES[crowd] || VO_FLOOR_CROWD_SIZES.roomy;
}

export function voFloorCrowdGap(crowd = "roomy") {
  return voFloorCrowdSpec(crowd).gap;
}

export function voFloorSeatBoxSize({ camOn = false, crowd = "roomy" } = {}) {
  const row = camOn ? voFloorCrowdSpec(crowd).live : voFloorCrowdSpec(crowd).idle;
  return {
    width: row.md[0],
    height: row.md[1],
    cam: row.cam,
    xs: row.xs,
    sm: row.sm,
    md: row.md,
  };
}

export function voFloorSeatFrameSx({ camOn = false, crowd = "roomy" } = {}) {
  const row = camOn ? voFloorCrowdSpec(crowd).live : voFloorCrowdSpec(crowd).idle;
  return {
    width: { xs: row.xs[0], sm: row.sm[0], md: row.md[0] },
    height: { xs: row.xs[1], sm: row.sm[1], md: row.md[1] },
    flex: "0 0 auto",
  };
}

export function floorPersonOpenTitle(name, { self = false, live = false } = {}) {
  const who = voFloorFirstName(name, self ? "Você" : "Colega");
  if (self) return live ? `Ver câmera de ${who} maior` : "";
  return `Clique para falar com ${who}`;
}

function ensureVoFloorSeatCss() {
  if (typeof document === "undefined") return;
  if (document.getElementById("vo-floor-seat-css")) return;
  const style = document.createElement("style");
  style.id = "vo-floor-seat-css";
  style.textContent = `
    [data-vo-floor-click="1"] { cursor: pointer !important; }
    [data-vo-floor-click="1"]:hover [data-cam-off-avatar],
    [data-vo-floor-click="1"]:hover video,
    [data-vo-floor-click="1"]:hover canvas {
      box-shadow: 0 0 0 3px rgba(232,196,138,0.92), 0 12px 28px rgba(0,0,0,0.4) !important;
    }
    [data-vo-floor-click="1"] [data-vo-click-hint] {
      opacity: 0;
      transform: translateX(-50%) translateY(6px);
    }
    [data-vo-floor-click="1"]:hover [data-vo-click-hint] {
      opacity: 1;
      transform: translateX(-50%) translateY(0);
    }
  `;
  document.head.appendChild(style);
}

function mediaFlagChipStyle(sizePx) {
  return {
    width: `${sizePx}px`,
    height: `${sizePx}px`,
    borderRadius: "50%",
    background: "rgba(36, 22, 18, 0.88)",
    border: "1px solid rgba(242,139,130,0.45)",
    color: "#f28b82",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 2px 8px rgba(0,0,0,0.32)",
    flexShrink: "0",
  };
}

function syncVoFloorMediaFlags(
  box,
  { muted = false, camOff = false, speakerMuted = false, camOn = false, crowd = "roomy" } = {}
) {
  if (!box) return;
  let flags = box.querySelector("[data-vo-media-flags]");
  if (!flags) {
    flags = document.createElement("div");
    flags.dataset.voMediaFlags = "1";
    box.appendChild(flags);
  }
  const place = voFloorMediaFlagsPlacement({ crowd, camOn });
  Object.assign(flags.style, {
    position: "absolute",
    left: "50%",
    top: camOn ? "6%" : "12%",
    transform: "translateX(-50%)",
    width: camOn ? "76%" : "62%",
    maxWidth: `${place.cam}px`,
    aspectRatio: "1 / 1",
    display: "block",
    overflow: "visible",
    zIndex: "7",
    pointerEvents: "none",
    boxSizing: "border-box",
  });
  flags.innerHTML = "";
  const items = voFloorMediaFlags({ muted, camOff, speakerMuted });
  const cluster = document.createElement("div");
  cluster.dataset.voMediaCluster = "1";
  flags.appendChild(cluster);
  Object.assign(cluster.style, {
    position: "absolute",
    right: `-${place.hangPx}px`,
    bottom: `-${place.hangPx}px`,
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: "2px",
    pointerEvents: "none",
  });
  for (const item of items) {
    const chip = document.createElement("span");
    chip.dataset.voFlag = item.key;
    chip.setAttribute("title", item.title);
    chip.setAttribute("aria-label", item.title);
    chip.innerHTML = mediaOffSvg(item.key, place.iconPx);
    applyStyle(chip, mediaFlagChipStyle(place.chipPx));
    cluster.appendChild(chip);
  }
  flags.style.display = items.length ? "block" : "none";
}

function markChipStyle(kind) {
  const birthday = kind === "birthday";
  return {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    maxWidth: "100%",
    padding: "3px 8px 3px 6px",
    borderRadius: "999px",
    background: birthday ? "rgba(62, 28, 40, 0.94)" : "rgba(42, 32, 18, 0.94)",
    border: birthday ? "1px solid rgba(243,196,216,0.7)" : "1px solid rgba(232,196,138,0.7)",
    color: birthday ? "#fde8f0" : "#f8edd9",
    fontSize: "0.62rem",
    fontWeight: "800",
    letterSpacing: "0.01em",
    lineHeight: "1.2",
    whiteSpace: "nowrap",
    boxShadow: "0 4px 12px rgba(0,0,0,0.38)",
  };
}

function syncFloorPersonMarks(box, { camOn = false, birthday = false, kudosEmoji = "", kudosLabel = "", kudosTitle = "", status = "present" } = {}) {
  if (!box) return;
  box.querySelectorAll("[data-vo-birthday], [data-vo-kudos], [data-vo-status]").forEach((el) => {
    if (!el.closest("[data-vo-floor-marks]")) el.remove();
  });
  let marks = box.querySelector("[data-vo-floor-marks]");
  if (!marks) {
    marks = document.createElement("div");
    marks.dataset.voFloorMarks = "1";
    box.appendChild(marks);
  }
  Object.assign(marks.style, {
    position: "absolute",
    left: "50%",
    top: camOn ? "4%" : "8%",
    transform: "translateX(-50%)",
    width: "90%",
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    gap: "4px",
    zIndex: "6",
    pointerEvents: "none",
  });
  marks.innerHTML = "";
  const statusMark = voCommStatusMark(status);
  if (statusMark) {
    const chip = document.createElement("span");
    chip.dataset.voStatus = "1";
    chip.setAttribute("title", statusMark.title);
    chip.setAttribute("aria-label", statusMark.title);
    Object.assign(chip.style, markChipStyle("kudos"));
    const icon = document.createElement("span");
    icon.setAttribute("aria-hidden", "true");
    icon.textContent = statusMark.emoji;
    const text = document.createElement("span");
    text.dataset.voMarkText = "1";
    text.textContent = statusMark.text;
    chip.append(icon, text);
    marks.appendChild(chip);
  }
  if (birthday) {
    const chip = document.createElement("span");
    chip.dataset.voBirthday = "1";
    chip.setAttribute("title", "Aniversário hoje");
    chip.setAttribute("aria-label", "Aniversário hoje");
    Object.assign(chip.style, markChipStyle("birthday"));
    chip.innerHTML =
      '<span aria-hidden="true">🎂</span><span data-vo-mark-text="1">Aniversário</span>';
    marks.appendChild(chip);
  }
  const emoji = String(kudosEmoji || "").trim();
  if (emoji) {
    const chip = document.createElement("span");
    chip.dataset.voKudos = "1";
    const title = String(kudosTitle || "").trim() || "Selo do dia";
    chip.setAttribute("title", title);
    chip.setAttribute("aria-label", title);
    Object.assign(chip.style, markChipStyle("kudos"));
    const word = String(kudosLabel || "Selo").trim() || "Selo";
    const icon = document.createElement("span");
    icon.setAttribute("aria-hidden", "true");
    icon.textContent = emoji;
    const text = document.createElement("span");
    text.dataset.voMarkText = "1";
    text.textContent = word;
    chip.append(icon, text);
    marks.appendChild(chip);
  }
  marks.style.display = marks.childElementCount ? "flex" : "none";

  let hint = box.querySelector("[data-vo-click-hint]");
  if (!hint) {
    hint = document.createElement("div");
    hint.dataset.voClickHint = "1";
    hint.textContent = "Falar";
    box.appendChild(hint);
  }
  Object.assign(hint.style, {
    position: "absolute",
    left: "50%",
    top: "auto",
    bottom: camOn ? "28%" : "32%",
    transform: "translateX(-50%)",
    zIndex: "9",
    pointerEvents: "none",
    padding: "2px 8px",
    borderRadius: "999px",
    background: "rgba(28, 22, 14, 0.92)",
    border: "1px solid rgba(232,196,138,0.45)",
    color: "#f8edd9",
    fontSize: "0.62rem",
    fontWeight: "800",
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    boxShadow: "0 4px 12px rgba(0,0,0,0.35)",
    transition: "opacity 140ms ease, transform 140ms ease",
  });
}

/** Primeira trilha de câmera ligada (ignora tela compartilhada). */
export function firstEnabledCameraTrack(participant, isScreenShareTrack) {
  let found = null;
  const visit = (pub) => {
    if (found || !pub) return;
    const track = pub.track;
    if (!track || track.kind !== "video") return;
    if (typeof isScreenShareTrack === "function" && isScreenShareTrack(track)) return;
    if (pub.isSubscribed === false) return;
    if (track.isEnabled === false) return;
    found = track;
  };
  participant?.videoTracks?.forEach?.(visit);
  if (!found) participant?.tracks?.forEach?.(visit);
  return found;
}

/** Copia o stream para um <video> extra (ampliação) sem detach do tile. */
export function attachPeekStream(track, videoEl) {
  if (!track || !videoEl) return () => {};
  try {
    const media = track.processedTrack || track.mediaStreamTrack;
    if (media && typeof MediaStream === "function") {
      videoEl.srcObject = new MediaStream([media]);
    }
  } catch {
    /* ignore */
  }
  videoEl.muted = true;
  videoEl.playsInline = true;
  videoEl.autoplay = true;
  videoEl.play?.().catch?.(() => {});
  return () => {
    try {
      videoEl.srcObject = null;
    } catch {
      /* ignore */
    }
  };
}

/**
 * Encaixa o quadro da câmera no tile sem cortar (contain + 16:9 no grid).
 * Escritório virtual: recorte circular no assento; câmera ligada fica maior.
 */
export function styleTileCameraMedia(el, { voFloor = false, camOn = false, crowd = "roomy" } = {}) {
  if (!el?.style) return el;
  if (voFloor) {
    const camPx = voFloorSeatBoxSize({ camOn, crowd }).cam;
    Object.assign(el.style, {
      borderRadius: "50%",
      objectFit: "cover",
      objectPosition: "center center",
      width: camOn ? "76%" : "62%",
      height: "auto",
      aspectRatio: "1 / 1",
      maxWidth: `${camPx}px`,
      maxHeight: `${camPx}px`,
      position: "absolute",
      inset: "auto",
      left: "50%",
      top: camOn ? "6%" : "12%",
      right: "auto",
      bottom: "auto",
      transform: "translateX(-50%)",
      boxShadow: camOn ? "0 12px 32px rgba(0,0,0,0.38)" : "0 8px 24px rgba(0,0,0,0.28)",
      background: "",
      display: "block",
    });
    return el;
  }
  Object.assign(el.style, {
    borderRadius: "",
    objectFit: "contain",
    objectPosition: "center center",
    width: "100%",
    height: "100%",
    position: "absolute",
    inset: "0",
    transform: "",
    boxShadow: "",
    background: "#000",
    display: "block",
  });
  return el;
}

export function ensureParticipantTileChrome(box) {
  if (!box || box.querySelector("[data-cam-off-overlay]")) return box;

  const overlay = document.createElement("div");
  overlay.dataset.camOffOverlay = "1";
  overlay.setAttribute("hidden", "");
  applyStyle(overlay, {
    position: "absolute",
    inset: "0",
    display: "none",
    alignItems: "center",
    justifyContent: "center",
    padding: "16px 20px 52px",
    background:
      "radial-gradient(ellipse at 50% 38%, rgba(95,99,104,0.55), transparent 58%), linear-gradient(180deg, #2d3134 0%, #202124 100%)",
    zIndex: "1",
    pointerEvents: "none",
  });
  overlay.innerHTML = `
    <div data-cam-off-stack style="display:flex;flex-direction:column;align-items:center;text-align:center;max-width:92%;gap:10px;">
      <div data-cam-off-avatar style="position:relative;width:96px;height:96px;border-radius:50%;background:#5f6368;overflow:hidden;box-shadow:0 10px 18px rgba(16,10,8,0.4);flex-shrink:0;">
        <img data-cam-off-photo alt="" style="width:100%;height:100%;object-fit:cover;display:none;" />
        <span data-cam-off-initials style="position:absolute;inset:0;display:flex;align-items:center;justify-content:center;color:#fff;font-weight:700;font-size:1.55rem;letter-spacing:0.02em;"></span>
        <span data-cam-off-badge style="position:absolute;right:0;bottom:0;width:28px;height:28px;border-radius:50%;background:#ea4335;color:#fff;display:flex;align-items:center;justify-content:center;border:2px solid #202124;box-shadow:0 2px 8px rgba(0,0,0,0.35);">${mediaOffSvg("camOff", 16)}</span>
      </div>
      <div data-cam-off-title style="color:#f1f3f4;font-size:1.05rem;font-weight:600;line-height:1.25;max-width:100%;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;"></div>
      <div data-cam-off-body style="color:#dadce0;font-size:0.82rem;line-height:1.4;max-width:280px;"></div>
    </div>
  `;
  box.appendChild(overlay);

  let label = box.querySelector("[data-video-name]");
  if (!label) {
    label = document.createElement("div");
    label.dataset.videoName = "1";
    box.appendChild(label);
  }
  applyStyle(label, {
    position: "absolute",
    left: "12px",
    bottom: "12px",
    padding: "4px 10px 4px 8px",
    fontSize: "12px",
    background: "rgba(32,33,36,0.82)",
    color: "#fff",
    borderRadius: "999px",
    zIndex: "2",
    maxWidth: "86%",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    lineHeight: "1.2",
  });

  if (!label.querySelector("[data-mic-off]")) {
    const mic = document.createElement("span");
    mic.dataset.micOff = "1";
    mic.setAttribute("hidden", "");
    mic.setAttribute("title", "Microfone desligado");
    mic.setAttribute("aria-label", "Microfone desligado");
    mic.innerHTML = mediaOffSvg("muted", 12);
    applyStyle(mic, {
      display: "none",
      width: "16px",
      height: "16px",
      color: "#f28b82",
      flexShrink: "0",
    });
    const text = document.createElement("span");
    text.dataset.videoNameText = "1";
    text.textContent = label.textContent || "";
    label.textContent = "";
    label.appendChild(mic);
    label.appendChild(text);
  }

  return box;
}

/** Proporção da câmera nos tiles da grade (evita o recorte do cover). */
export const CAMERA_TILE_ASPECT = "16 / 9";

export function applySeatBoxStyle(box, voFloor = false, { camOn = false } = {}) {
  if (!box) return;
  const nameEl = box.querySelector("[data-video-name]");
  if (voFloor) {
    Object.assign(box.style, {
      background: "transparent",
      borderRadius: "28px",
      boxShadow: "none",
      aspectRatio: "",
      width: "",
      height: "",
      maxWidth: "",
      maxHeight: "",
      minWidth: "",
      minHeight: "",
      flex: "0 0 auto",
      overflow: "visible",
    });
    if (nameEl) {
      nameEl.style.display = "none";
      nameEl.style.visibility = "hidden";
      nameEl.setAttribute("aria-hidden", "true");
    }
  } else {
    Object.assign(box.style, {
      background: "#000",
      borderRadius: "16px",
      boxShadow: "",
      position: "relative",
      aspectRatio: CAMERA_TILE_ASPECT,
      width: "100%",
      height: "auto",
      maxWidth: "100%",
      maxHeight: "100%",
      minWidth: "0",
      minHeight: "0",
      flex: "",
      overflow: "hidden",
    });
    if (nameEl) {
      nameEl.style.display = "inline-flex";
      nameEl.style.visibility = "visible";
      nameEl.removeAttribute("aria-hidden");
    }
  }
}

export function syncParticipantTile(box, { name, avatarUrl, camOff, muted, speakerMuted = false, voFloor = false, status = "present", birthday = false, kudosEmoji = "", kudosLabel = "", kudosTitle = "", cargo = "", crowd = "roomy", joinedAt = null, now = Date.now(), timeZone = DEFAULT_CLOCK_TZ } = {}) {
  if (!box) return;
  ensureParticipantTileChrome(box);
  if (voFloor) ensureVoFloorSeatCss();
  applySeatBoxStyle(box, voFloor, { camOn: !camOff });
  if (voFloor) box.dataset.voCrowd = crowd;
  const overlay = box.querySelector("[data-cam-off-overlay]");
  const photo = box.querySelector("[data-cam-off-photo]");
  const initialsEl = box.querySelector("[data-cam-off-initials]");
  const titleEl = box.querySelector("[data-cam-off-title]");
  const bodyEl = box.querySelector("[data-cam-off-body]");
  const badge = box.querySelector("[data-cam-off-badge]");
  const mic = box.querySelector("[data-mic-off]");
  const nameText = box.querySelector("[data-video-name-text]");
  const label = String(name || "Participante");
  const copy = voFloor
    ? floorPresenceCopy({
        self: false,
        name: label,
        status,
        compact: crowd === "busy" || crowd === "packed",
        cargo,
        joinedAt,
        now,
        timeZone,
      })
    : cameraOffPresenceCopy({ self: false, name: label, muted: Boolean(muted) });
  const compact = Boolean(box.clientHeight && box.clientHeight < 180);

  if (nameText) nameText.textContent = label;
  else {
    const nameNode = box.querySelector("[data-video-name]");
    if (nameNode && !nameNode.querySelector("[data-video-name-text]")) {
      nameNode.textContent = label;
    }
  }

  if (mic) {
    mic.hidden = voFloor || !muted;
    mic.style.display = !voFloor && muted ? "inline-flex" : "none";
  }

  if (voFloor) {
    syncVoFloorMediaFlags(box, {
      muted: Boolean(muted),
      camOff: Boolean(camOff),
      speakerMuted: Boolean(speakerMuted),
      camOn: !camOff,
      crowd,
    });
  } else {
    const leftoverFlags = box.querySelector("[data-vo-media-flags]");
    if (leftoverFlags) leftoverFlags.style.display = "none";
  }

  if (overlay) {
    overlay.hidden = !camOff;
    overlay.style.display = camOff ? "flex" : "none";
    if (voFloor) {
      overlay.style.background = "transparent";
      overlay.style.padding = "8px 8px 12px";
      const face = overlay.querySelector("[data-cam-off-avatar]");
      if (face) {
        Object.assign(face.style, {
          width: "62%",
          height: "auto",
          aspectRatio: "1 / 1",
          maxWidth: `${voFloorSeatBoxSize({ camOn: false, crowd }).cam}px`,
        });
      }
    } else {
      overlay.style.background =
        "radial-gradient(ellipse at 50% 38%, rgba(95,99,104,0.55), transparent 58%), linear-gradient(180deg, #2d3134 0%, #202124 100%)";
      overlay.style.padding = "16px 20px 52px";
    }
  }

  if (badge) badge.style.display = voFloor ? "none" : "flex";

  const sealEmoji = String(kudosEmoji || "").trim();
  if (voFloor) {
    syncFloorPersonMarks(box, {
      camOn: !camOff,
      birthday,
      kudosEmoji: sealEmoji,
      kudosLabel,
      kudosTitle,
      status,
    });
  } else {
    const leftover = box.querySelector("[data-vo-floor-marks]");
    if (leftover) leftover.style.display = "none";
    const hint = box.querySelector("[data-vo-click-hint]");
    if (hint) hint.style.display = "none";
  }

  box.querySelectorAll("video, canvas").forEach((el) => {
    el.style.opacity = camOff ? "0" : "1";
    styleTileCameraMedia(el, { voFloor, camOn: !camOff, crowd });
  });

  if (voFloor) {
    const live = !camOff;
    box.dataset.voLiveCam = live ? "1" : "0";
    box.dataset.voFloorClick = "1";
    box.style.cursor = "pointer";
    box.title = floorPersonOpenTitle(label, { live });
    box.setAttribute("aria-label", box.title);
    box.setAttribute("role", "button");
    box.tabIndex = 0;
  } else {
    box.removeAttribute("data-vo-floor-click");
  }

  if (titleEl) {
    titleEl.textContent = voFloor ? voFloorFirstName(label) : copy.title;
    if (voFloor) titleEl.style.display = "none";
  }
  if (bodyEl) {
    bodyEl.textContent = voFloor
      ? birthday
        ? "Aniversário"
        : sealEmoji
          ? "Selo do dia"
          : copy.body
      : compact
        ? muted
          ? "Câmera e microfone desligados"
          : "Câmera desligada"
        : copy.body;
    bodyEl.style.display = voFloor ? "none" : camOff ? "block" : "none";
  }

  let plate = box.querySelector("[data-vo-seat-plate]");
  if (voFloor) {
    if (!plate) {
      plate = document.createElement("div");
      plate.dataset.voSeatPlate = "1";
      Object.assign(plate.style, {
        position: "absolute",
        left: "8px",
        right: "8px",
        bottom: "8px",
        zIndex: "5",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "8px",
        pointerEvents: "none",
        textAlign: "center",
      });
      const plateName = document.createElement("div");
      plateName.dataset.voSeatName = "1";
      Object.assign(plateName.style, {
        color: "#f8f4ee",
        fontSize: "0.95rem",
        fontWeight: "700",
        lineHeight: "1.2",
        maxWidth: "100%",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        textShadow: "0 1px 2px rgba(16,10,8,0.75)",
      });
      const plateRole = document.createElement("div");
      plateRole.dataset.voSeatRole = "1";
      Object.assign(plateRole.style, {
        color: "#d7cfc4",
        fontSize: "0.72rem",
        fontWeight: "600",
        lineHeight: "1.2",
        maxWidth: "100%",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        textShadow: "0 1px 2px rgba(16,10,8,0.7)",
      });
      const plateTime = document.createElement("div");
      plateTime.dataset.voSeatConnected = "1";
      Object.assign(plateTime.style, {
        color: "#c9c0b4",
        fontSize: "0.64rem",
        fontWeight: "600",
        lineHeight: "1.2",
        maxWidth: "100%",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        textShadow: "0 1px 2px rgba(16,10,8,0.7)",
      });
      plate.append(plateName, plateRole, plateTime);
      box.appendChild(plate);
    }
    const plateName = plate.querySelector("[data-vo-seat-name]");
    const plateRole = plate.querySelector("[data-vo-seat-role]");
    let plateTime = plate.querySelector("[data-vo-seat-connected]");
    if (!plateTime) {
      plateTime = document.createElement("div");
      plateTime.dataset.voSeatConnected = "1";
      Object.assign(plateTime.style, {
        color: "#c9c0b4",
        fontSize: "0.64rem",
        fontWeight: "600",
        lineHeight: "1.2",
        maxWidth: "100%",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        textShadow: "0 1px 2px rgba(16,10,8,0.7)",
      });
      plate.appendChild(plateTime);
    }
    if (plateName) plateName.textContent = voFloorFirstName(label);
    if (plateRole) {
      plateRole.textContent = copy.body;
    }
    if (plateTime) {
      const packed = crowd === "packed" || crowd === "busy";
      plateTime.textContent = packed ? "" : copy.connected || "";
      plateTime.style.display = plateTime.textContent ? "block" : "none";
    }
    plate.style.display = "flex";
  } else if (plate) {
    plate.style.display = "none";
  }

  const src = sanitizeAvatarUrl(avatarUrl);
  if (photo) {
    if (src) {
      photo.src = src;
      photo.alt = label;
      photo.style.display = "block";
      photo.onerror = () => {
        photo.style.display = "none";
        if (initialsEl) initialsEl.style.display = "flex";
      };
      if (initialsEl) initialsEl.style.display = "none";
    } else {
      photo.removeAttribute("src");
      photo.style.display = "none";
      if (initialsEl) initialsEl.style.display = "flex";
    }
  }
  if (initialsEl) initialsEl.textContent = videoInitials(label);
}

export function syncTileFocus(box, { speaking = false, pinned = false, spotlight = false, hiddenOverflow = false } = {}) {
  if (!box) return;
  box.dataset.speaking = speaking ? "1" : "0";
  box.dataset.pinned = pinned ? "1" : "0";
  box.dataset.spotlight = spotlight ? "1" : "0";
  box.dataset.hiddenOverflow = hiddenOverflow ? "1" : "0";
  if (speaking) box.setAttribute("aria-current", "true");
  else box.removeAttribute("aria-current");
}

function safeRemoveNode(node) {
  try {
    node?.remove?.();
  } catch {
    /* ignore */
  }
}

/** Remove todos os tiles remotos do mapa e órfãos que ficaram na grade. */
export function clearRemoteParticipantTiles(containerMap, grid) {
  if (containerMap && typeof containerMap.forEach === "function") {
    containerMap.forEach((box) => safeRemoveNode(box));
    containerMap.clear();
  }
  if (!grid) return;
  const orphans = grid.querySelectorAll
    ? grid.querySelectorAll("[data-sid]")
    : [];
  orphans.forEach((el) => safeRemoveNode(el));
}

/**
 * Um colega reconectado ganha SID (e às vezes identity) novo.
 * No piso, o mesmo userId em dois tiles parece que a pessoa entrou de novo.
 */
export function evictRemoteTilesForIdentity(
  containerMap,
  { identity, keepSid, sameUser = false } = {}
) {
  const want = String(identity || "").trim();
  if (!want || !containerMap || typeof containerMap.forEach !== "function") return 0;
  const wantUid = sameUser ? Number(userIdFromAgentIdentity(want) || 0) : 0;
  const stale = [];
  containerMap.forEach((box, sid) => {
    if (keepSid && sid === keepSid) return;
    const boxId = String(box?.dataset?.identity || "").trim();
    if (boxId && boxId === want) {
      stale.push(sid);
      return;
    }
    if (wantUid > 0 && Number(userIdFromAgentIdentity(boxId) || 0) === wantUid) {
      stale.push(sid);
    }
  });
  stale.forEach((sid) => {
    safeRemoveNode(containerMap.get(sid));
    containerMap.delete(sid);
  });
  return stale.length;
}

/** userIds + identities já sentados em tiles Twilio — o ghost React não pode repetir a mesma pessoa. */
export function collectRemoteSeatKeys(containerMap) {
  const userIds = [];
  const identities = [];
  const seenUid = new Set();
  const seenIdent = new Set();
  if (!containerMap || typeof containerMap.forEach !== "function") {
    return { userIds, identities };
  }
  containerMap.forEach((box) => {
    if (String(box?.dataset?.voFloorHidden || "") === "1") return;
    const ident = String(box?.dataset?.identity || "").trim();
    if (ident && !seenIdent.has(ident)) {
      seenIdent.add(ident);
      identities.push(ident);
    }
    const uid = Number(userIdFromAgentIdentity(ident) || 0);
    if (uid > 0 && !seenUid.has(uid)) {
      seenUid.add(uid);
      userIds.push(uid);
    }
  });
  return { userIds, identities };
}

/** Silencia só a reprodução local (`<audio>` remoto). Não mexe no microfone. */
export function applyRemoteSpeakerMute(roots, muted) {
  const list = Array.isArray(roots) ? roots : [roots];
  const on = Boolean(muted);
  for (const root of list) {
    if (!root?.querySelectorAll) continue;
    root.querySelectorAll("audio").forEach((el) => {
      el.muted = on;
    });
  }
}
